import {
    RecoilValueReadOnly,
    ReadOnlySelectorOptions,
    ReadWriteSelectorOptions,
    selector,
    useRecoilState,
    SetterOrUpdater,
} from 'recoil';
import { atomFactory } from './atomFactory';

export type SelectorFactoryR<T> = {
    selector: RecoilValueReadOnly<T>;
    hook: () => {
        get: T;
        set: SetterOrUpdater<T>;
        invalidateCache: () => void;
    };
};

export type SelectorFactoryParam<T> = {
    key: string;
    get: ReadOnlySelectorOptions<T>['get'];
    set?: ReadWriteSelectorOptions<T>['set'];
};

export const selectorFactory = <T>(
    selectorParam: SelectorFactoryParam<T>,
): SelectorFactoryR<T> => {
    const { key, get, set } = selectorParam;
    // hack to force invalidation of cache from...
    // ... https://github.com/facebookexperimental/Recoil/issues/85#issuecomment-629862897
    const { atom: forceUpdate, hook: useForceUpdate } = atomFactory({
        key: `${key}-force-update-selector-atom`,
        default: 0,
    });

    const stateSelector = selector<T>({
        key,
        get: (...args) => {
            args[0].get(forceUpdate);
            return get(...args);
        },
        set: set || (() => {}),
    });

    const useFactorySelector = () => {
        const { value: count, set: increment } = useForceUpdate();
        const invalidateCache = () => increment(count + 1);
        const [value, setValue] = useRecoilState(stateSelector);
        return {
            invalidateCache,
            get: value,
            set: setValue,
        };
    };

    return {
        selector: stateSelector,
        hook: useFactorySelector,
    };
};

export default selectorFactory;
